import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getAppPortalRoot } from "../utils/appPortal";
import { X } from "lucide-react";
import { useCustomScrollbar } from "../hooks/useCustomScrollbar";
import { computeQuestJournalStats } from "../utils/questJournalStats";
import "./DeleteWordModal.css";
import "./QuestJournalStatsModal.css";

function StatsRow({ label, value }) {
  return (
    <div className="quest-journal-stats-modal__row">
      <span className="quest-journal-stats-modal__row-label">{label}</span>
      <span className="quest-journal-stats-modal__row-value">{value}</span>
    </div>
  );
}

function StatsSectionTitle({ children }) {
  return <h3 className="quest-journal-stats-modal__section-title">{children}</h3>;
}

function StatsBreakdownPanel({ title, items, emptyLabel }) {
  const scrollRef = useRef(null);
  const { trackRef, scrollbar, updateScrollbar, handleThumbPointerDown, handleTrackPointerDown } =
    useCustomScrollbar(scrollRef);
  const [scrollFades, setScrollFades] = useState({ top: false, bottom: false });

  const updateScrollUi = useCallback(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollEl;
    const threshold = 4;
    const hasOverflow = scrollHeight > clientHeight + 1;

    setScrollFades({
      top: hasOverflow && scrollTop > threshold,
      bottom: hasOverflow && scrollTop + clientHeight < scrollHeight - threshold,
    });
    updateScrollbar();
  }, [updateScrollbar]);

  useEffect(() => {
    updateScrollUi();
  }, [items.length, updateScrollUi]);

  return (
    <div className="quest-journal-stats-modal__breakdown">
      <StatsSectionTitle>{title}</StatsSectionTitle>
      <div className="quest-journal-stats-modal__breakdown-viewport">
        <div className="quest-journal-stats-modal__breakdown-scroller">
          <div
            ref={scrollRef}
            className="quest-journal-stats-modal__breakdown-scroll"
            onScroll={updateScrollUi}
          >
            {items.length ? (
              <ul className="quest-journal-stats-modal__breakdown-list">
                {items.map((item) => (
                  <li key={item.label} className="quest-journal-stats-modal__breakdown-item">
                    <span className="quest-journal-stats-modal__breakdown-label">{item.label}</span>
                    <span className="quest-journal-stats-modal__breakdown-value">
                      {item.quantity.toLocaleString("fr-FR")}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="quest-journal-stats-modal__breakdown-empty">{emptyLabel}</p>
            )}
          </div>
          <div
            ref={trackRef}
            className={[
              "quest-journal-stats-modal__scrollbar",
              !scrollbar.visible && "quest-journal-stats-modal__scrollbar--hidden",
            ]
              .filter(Boolean)
              .join(" ")}
            aria-hidden="true"
            onPointerDown={handleTrackPointerDown}
          >
            {scrollbar.visible ? (
              <div
                className="quest-journal-stats-modal__scrollbar-thumb"
                style={{
                  height: `${scrollbar.height}px`,
                  transform: `translateY(${scrollbar.top}px)`,
                }}
                onPointerDown={handleThumbPointerDown}
              />
            ) : null}
          </div>
        </div>
        {scrollFades.top ? (
          <div className="quest-journal-stats-modal__fade quest-journal-stats-modal__fade--top" aria-hidden="true" />
        ) : null}
        {scrollFades.bottom ? (
          <div
            className="quest-journal-stats-modal__fade quest-journal-stats-modal__fade--bottom"
            aria-hidden="true"
          />
        ) : null}
      </div>
    </div>
  );
}

export default function QuestJournalStatsModal({ open, entries = [], onClose }) {
  const stats = useMemo(() => computeQuestJournalStats(entries), [entries]);

  if (!open) return null;

  return createPortal(
    <div className="delete-word-modal-backdrop" onMouseDown={onClose}>
      <div
        className="delete-word-modal quest-journal-stats-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="quest-journal-stats-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="quest-journal-stats-modal__close-wrap">
          <button
            type="button"
            className="quest-journal-stats-modal__close"
            onClick={onClose}
            aria-label="Fermer"
          >
            <X size={16} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>

        <h2 id="quest-journal-stats-title" className="quest-journal-stats-modal__title">
          Statistiques de quêtes
        </h2>

        <p className="quest-journal-stats-modal__intro">
          {stats.questCount.toLocaleString("fr-FR")}{" "}
          {stats.questCount > 1 ? "quêtes prises en compte" : "quête prise en compte"}
          {" "}
          (quête en cours incluse si terminée).
        </p>

        <div className="quest-journal-stats-modal__body">
          <div className="quest-journal-stats-modal__column">
            <div className="quest-journal-stats-modal__panel">
              <StatsSectionTitle>Taux et participation</StatsSectionTitle>
              <StatsRow label="Taux de réussite" value={stats.successRate} />
              <StatsRow label="Taux de participation" value={stats.participationRate} />
              <StatsRow label="Taux de renoncement" value={stats.abandonmentRate} />
            </div>

            <div className="quest-journal-stats-modal__panel">
              <StatsSectionTitle>Lecture</StatsSectionTitle>
              <StatsRow label="Temps de lecture validé" value={stats.readingValidatedLabel} />
              <StatsRow label="Temps de lecture renoncé" value={stats.readingRenouncedLabel} />
              <StatsRow
                label="Fidélité de l'objectif de lecture"
                value={stats.readingFidelityRate}
              />
            </div>

            <div className="quest-journal-stats-modal__panel">
              <StatsSectionTitle>Objectifs cumulés</StatsSectionTitle>
              <StatsRow label="Mots capturés" value={stats.captureRatio} />
              <StatsRow
                label="Fidélité de l'objectif de capture"
                value={stats.captureFidelityRate}
              />
              <StatsRow label="Parties de mémoire jouées" value={stats.memoryRatio} />
              <StatsRow
                label="Fidélité de l'objectif de jeu de mémoire"
                value={stats.memoryFidelityRate}
              />
              <StatsRow
                label="Parties jouées (sans critère de score)"
                value={stats.playGamesRatio}
              />
              <StatsRow
                label="Fidélité de l'objectif de parties à jouer"
                value={stats.playGamesFidelityRate}
              />
            </div>

            <div className="quest-journal-stats-modal__panel quest-journal-stats-modal__panel--compact">
              <StatsRow
                label="Instructeurs uniques rencontrés"
                value={stats.instructorsCountLabel}
              />
              <StatsRow label="Reliques découvertes" value={stats.relicsCountLabel} />
            </div>
          </div>

          <div className="quest-journal-stats-modal__column">
            <div className="quest-journal-stats-modal__breakdown-grid">
              <StatsBreakdownPanel
                title={`Pierres gagnées (${stats.earnedGemsTotal.toLocaleString("fr-FR")})`}
                items={stats.earnedGems}
                emptyLabel="Aucune pierre gagnée sur cette sélection."
              />
              <StatsBreakdownPanel
                title={`Potions gagnées (${stats.earnedPotionsTotal.toLocaleString("fr-FR")})`}
                items={stats.earnedPotions}
                emptyLabel="Aucune potion gagnée sur cette sélection."
              />
            </div>

            <div className="quest-journal-stats-modal__panel">
              <StatsSectionTitle>Probabilité de découverte (relique)</StatsSectionTitle>
              <StatsRow
                label="Meilleure probabilité de relique"
                value={stats.relicChanceMaxLabel}
              />
              <StatsRow label="Pire probabilité de relique" value={stats.relicChanceMinLabel} />
            </div>

            <div className="quest-journal-stats-modal__breakdown-grid">
              <StatsBreakdownPanel
                title="Pierres proposées"
                items={stats.offeredGems}
                emptyLabel="Aucune pierre proposée sur cette sélection."
              />
              <StatsBreakdownPanel
                title="Potions proposées"
                items={stats.offeredPotions}
                emptyLabel="Aucune potion proposée sur cette sélection."
              />
            </div>
          </div>
        </div>
      </div>
    </div>,
    getAppPortalRoot()
  );
}
